import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { RadioComponent } from '../../shared/components/radio/radio.component';
import { HttpClientService } from '../../shared/services/http-client.service';
import { AppStateService } from '../../shared/services/app-state.service';
import { Questionario, Opcoes, RespostaQuestionario, RespostaQuestionarioPayload } from '../../core/interface/questionario';
import { PerfilRecomendado } from '../../core/interface/perfil-recomendado';
import { PerfilParticipante } from '../../core/interface/perfil-participante';
import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';
import { CampanhaPerfil } from '../../core/interface/campanha';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'app-questionnaire',
  standalone: true,
  imports: [CommonModule, RadioComponent],
  templateUrl: './questionnaire.component.html',
  styleUrl: './questionnaire.component.scss',
})
export class Questionnaire implements OnInit {
  perfilRecomendado!: PerfilRecomendado;
  questionnaire!: Questionario[];
  perfilParticipante!: PerfilParticipante;
  campanha!: CampanhaPerfil;
  answers: Record<number, number | undefined> = {};
  // Evita cliques repetidos no botão "Seguinte" enquanto o salvamento ocorre.
  isSaving = false;

  constructor(
    private router: Router,
    private httpClientService: HttpClientService,
    private appStateService: AppStateService,
    private loaderService: LoaderService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const campanha = this.appStateService.getCampanha();
    const perfilParticipante = this.appStateService.getPerfilParticipante();

    if (!campanha || !perfilParticipante) {
      console.warn('Home carregado sem estado completo:', { campanha, perfilParticipante });
      this.router.navigate(['/not-found']);
      return;
    }
    this.campanha = campanha
    this.perfilParticipante = perfilParticipante;
    this.getQuestionnaire(this.campanha.idQuestionario);
    this.answers = this.appStateService.getQuestionnaireAnswers();
  }

  get allAnswered(): boolean {
    return (
      Array.isArray(this.questionnaire) &&
      this.questionnaire.length > 0 &&
      this.questionnaire.every((question) => this.answers[question.idPergunta] !== undefined)
    );
  }

  selectOption(event: Event, questionId: number, optionId: number): void {

    const input = event.target as HTMLInputElement;

    if (input.checked) {

      this.answers[questionId] = optionId;

    } else {

      delete this.answers[questionId];
    }

    this.appStateService.setQuestionnaireAnswers(
      this.answers
    );
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  goForward(): void {
    this.appStateService.setQuestionnaireAnswers(this.answers);

    if (this.perfilParticipante.id) {
      this.saveQuestionnaire(this.perfilParticipante.id, this.montarRespostas());
    }
  }

  /**
   * Converte o mapa de respostas ({ idPergunta: idAlternativa }) no array
   * esperado pelo endpoint: [{ idPergunta, idAlternativaSel }].
   */
  private montarRespostas(): RespostaQuestionario[] {
    return Object.entries(this.answers)
      .filter(([, idAlternativaSel]) => idAlternativaSel !== undefined)
      .map(([idPergunta, idAlternativaSel]) => ({
        idPergunta: Number(idPergunta),
        idAlternativaSel: Number(idAlternativaSel),
      }));
  }

  private getQuestionnaire(idQuestionario: number): void {
    const token = this.appStateService.getToken() ?? '';

    this.loaderService.showLoader();
    this.httpClientService.getQuestionnaire(token, idQuestionario)
      .then(response => {

        if (response.status === EnumStatusResponse.Success) {
          const body: Questionario[] = response.body as Questionario[];

          //ordenando as perguntas e alternativas caso não venham ordenadas do backend
          this.questionnaire = body.map((question: Questionario, questionIndex: number) => ({
            ...question,
            ordem: question.ordem ?? questionIndex + 1,
            alternativas: question.alternativas.map((option: Opcoes, optionIndex: number) => ({
              ...option,
              ordem: option.ordem ?? optionIndex + 1
            }))
          }));
        }
      })
      .catch(error => {
        console.error('Erro ao carregar o Questionnaire:', error);
        this.toastService.show('Não foi possível carregar o questionário. Tente novamente.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }

  private saveQuestionnaire(idParticipante: number, respostas: RespostaQuestionario[]){
    const questionarioPayload: RespostaQuestionarioPayload = {
      respostas: respostas
    };

    const token = this.appStateService.getToken() ?? '';

    this.isSaving = true;
    this.loaderService.showLoader();
    this.httpClientService.saveQuestionnaire(token, idParticipante, questionarioPayload)
      .then(response => {

        if(response.status === EnumStatusResponse.Created){
          this.toastService.show('Questionário enviado com sucesso.', 'success');
          this.router.navigate(['/profile-selection']);
        }
      })
      .catch(error => {
        console.error('Erro ao salvar o Questionnaire:', error);
        this.toastService.show('Não foi possível salvar suas respostas. Tente novamente.', 'error');
      })
      .finally(() => {
        this.isSaving = false;
        this.loaderService.hideLoader();
      });
  }
}
